import { useState } from "react";
import { DealerLayout } from "@/components/shared/DealerLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { uploadPreApprovedDocument } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Download, Save, Loader2 } from "lucide-react";
import jsPDF from "jspdf";

const ENTITY_TYPES = [
  { value: "individual", label: "Individual/sole proprietor" },
  { value: "c_corp", label: "C Corporation" },
  { value: "s_corp", label: "S Corporation" },
  { value: "partnership", label: "Partnership" },
  { value: "trust", label: "Trust/estate" },
  { value: "llc", label: "LLC" },
  { value: "other", label: "Other" },
];

export default function W9Generator() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "", business_name: "", entity_type: "individual", llc_class: "",
    exemptions: "", fatca: "", address: "", city_state_zip: "",
    ssn: "", ein: "",
  });

  const u = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  const generatePDF = () => {
    const pdf = new jsPDF({ unit: "mm", format: "letter" });
    const W = 215.9, margin = 15;

    // Header
    pdf.setFillColor(15, 23, 42);
    pdf.rect(0, 0, W, 22, "F");
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(14);
    pdf.setFont("helvetica", "bold");
    pdf.text("Form W-9", margin, 10);
    pdf.setFontSize(8);
    pdf.setFont("helvetica", "normal");
    pdf.text("Request for Taxpayer Identification Number and Certification", margin, 16);
    pdf.text("(Rev. October 2018) - Department of the Treasury - Internal Revenue Service", margin, 20);

    let y = 30;
    const drawField = (label, value, x, w) => {
      pdf.setFontSize(7);
      pdf.setTextColor(100, 100, 100);
      pdf.text(label, x, y);
      pdf.setFontSize(10);
      pdf.setTextColor(30, 30, 30);
      pdf.setFont("helvetica", "bold");
      pdf.text(value || "", x, y + 5);
      pdf.setFont("helvetica", "normal");
      pdf.setDrawColor(200, 200, 200);
      pdf.line(x, y + 7, x + w, y + 7);
    };

    drawField("1. Name (as shown on your income tax return)", form.name, margin, W - margin * 2);
    y += 14;
    drawField("2. Business name/disregarded entity name, if different from above", form.business_name, margin, W - margin * 2);
    y += 14;

    // Entity type
    pdf.setFontSize(7);
    pdf.setTextColor(100, 100, 100);
    pdf.text("3. Federal tax classification:", margin, y);
    y += 5;
    const selected = ENTITY_TYPES.find((e) => e.value === form.entity_type);
    pdf.setFontSize(9);
    pdf.setTextColor(30, 30, 30);
    pdf.setFont("helvetica", "bold");
    pdf.text(selected?.label || "", margin, y);
    pdf.setFont("helvetica", "normal");
    if (form.entity_type === "llc" && form.llc_class) {
      pdf.text(`  (Tax classification: ${form.llc_class})`, margin + 20, y);
    }
    y += 10;

    drawField("4. Exemptions (codes apply only to certain entities)", form.exemptions, margin, (W - margin * 2) / 2 - 5);
    drawField("FATCA reporting code", form.fatca, margin + (W - margin * 2) / 2 + 5, (W - margin * 2) / 2 - 5);
    y += 14;
    drawField("5. Address (number, street, and apt. or suite no.)", form.address, margin, W - margin * 2);
    y += 14;
    drawField("6. City, state, and ZIP code", form.city_state_zip, margin, W - margin * 2);
    y += 18;

    // TIN
    pdf.setFillColor(240, 240, 240);
    pdf.rect(margin, y, W - margin * 2, 20, "F");
    pdf.setFontSize(8);
    pdf.setTextColor(30, 30, 30);
    pdf.setFont("helvetica", "bold");
    pdf.text("Part I - Taxpayer Identification Number (TIN)", margin + 3, y + 5);
    pdf.setFont("helvetica", "normal");
    y += 10;
    if (form.ssn) {
      pdf.setFontSize(7);
      pdf.setTextColor(100, 100, 100);
      pdf.text("Social Security Number:", margin + 3, y);
      pdf.setFontSize(10);
      pdf.setTextColor(30, 30, 30);
      pdf.text(form.ssn, margin + 50, y);
    }
    if (form.ein) {
      pdf.setFontSize(7);
      pdf.setTextColor(100, 100, 100);
      pdf.text("Employer ID Number:", margin + 100, y);
      pdf.setFontSize(10);
      pdf.setTextColor(30, 30, 30);
      pdf.text(form.ein, margin + 145, y);
    }
    y += 16;

    // Certification
    pdf.setFontSize(8);
    pdf.setTextColor(30, 30, 30);
    pdf.setFont("helvetica", "bold");
    pdf.text("Part II - Certification", margin, y);
    pdf.setFont("helvetica", "normal");
    y += 5;
    pdf.setFontSize(7);
    pdf.setTextColor(80, 80, 80);
    const cert = "Under penalties of perjury, I certify that: 1. The number shown on this form is my correct taxpayer identification number, 2. I am not subject to backup withholding, 3. I am a U.S. citizen or other U.S. person, 4. The FATCA code(s) entered on this form (if any) indicating that I am exempt from FATCA reporting is correct.";
    const lines = pdf.splitTextToSize(cert, W - margin * 2);
    lines.forEach((line) => { pdf.text(line, margin, y); y += 3.5; });
    y += 8;

    // Signature line
    pdf.setDrawColor(30, 30, 30);
    pdf.line(margin, y, margin + 120, y);
    pdf.line(margin + 130, y, W - margin, y);
    pdf.setFontSize(7);
    pdf.text("Signature of U.S. person", margin, y + 4);
    pdf.text("Date", margin + 130, y + 4);
    pdf.text(new Date().toLocaleDateString(), margin + 140, y - 2);

    // Footer
    pdf.setFontSize(6);
    pdf.setTextColor(150, 150, 150);
    pdf.text("Generated for Prime Media Wireless - New Retailer Application", margin, 270);

    return pdf;
  };

  const handleDownload = () => {
    if (!form.name) { toast.error("Please enter your name"); return; }
    generatePDF().save("W9_Form.pdf");
    toast.success("W9 downloaded");
  };

  const handleSave = async () => {
    if (!form.name) { toast.error("Please enter your name"); return; }
    setSaving(true);
    try {
      const blob = generatePDF().output("blob");
      const file = new File([blob], "W9_Form.pdf", { type: "application/pdf" });
      await uploadPreApprovedDocument(file, "w9_form");
      toast.success("W9 saved to your documents");
      navigate("/dealer/documents");
    } catch { toast.error("Failed to save"); }
    setSaving(false);
  };

  return (
    <DealerLayout>
      <div className="max-w-3xl mx-auto" data-testid="w9-generator-page">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dealer/pre-approved-documents")} data-testid="w9-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading font-bold text-2xl text-navy">W-9 Form Generator</h1>
            <p className="text-slate-500 font-body text-sm">Fill in your information to generate a W-9 form</p>
          </div>
        </div>

        <div className="space-y-4">
          <Card className="border-slate-200">
            <CardContent className="p-5 space-y-4">
              <div><Label className="text-xs text-slate-500">1. Name (as shown on your income tax return) *</Label>
                <Input value={form.name} onChange={(e) => u("name", e.target.value)} className="mt-1" data-testid="w9-name" /></div>
              <div><Label className="text-xs text-slate-500">2. Business name / disregarded entity name</Label>
                <Input value={form.business_name} onChange={(e) => u("business_name", e.target.value)} className="mt-1" data-testid="w9-business" /></div>
              <div>
                <Label className="text-xs text-slate-500">3. Federal tax classification</Label>
                <Select value={form.entity_type} onValueChange={(v) => u("entity_type", v)}>
                  <SelectTrigger className="mt-1" data-testid="w9-entity-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{ENTITY_TYPES.map((e) => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {form.entity_type === "llc" && (
                <div><Label className="text-xs text-slate-500">LLC Tax Classification (C, S, or P)</Label>
                  <Input value={form.llc_class} onChange={(e) => u("llc_class", e.target.value)} className="mt-1" placeholder="C, S, or P" /></div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-xs text-slate-500">4. Exemptions</Label>
                  <Input value={form.exemptions} onChange={(e) => u("exemptions", e.target.value)} className="mt-1" /></div>
                <div><Label className="text-xs text-slate-500">FATCA reporting code</Label>
                  <Input value={form.fatca} onChange={(e) => u("fatca", e.target.value)} className="mt-1" /></div>
              </div>
              <div><Label className="text-xs text-slate-500">5. Address (number, street, apt/suite)</Label>
                <Input value={form.address} onChange={(e) => u("address", e.target.value)} className="mt-1" data-testid="w9-address" /></div>
              <div><Label className="text-xs text-slate-500">6. City, state, and ZIP code</Label>
                <Input value={form.city_state_zip} onChange={(e) => u("city_state_zip", e.target.value)} className="mt-1" data-testid="w9-city" /></div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-5 space-y-4">
              <h3 className="font-heading font-semibold text-navy text-sm">Part I - Taxpayer Identification Number</h3>
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-xs text-slate-500">Social Security Number</Label>
                  <Input value={form.ssn} onChange={(e) => u("ssn", e.target.value)} className="mt-1" placeholder="XXX-XX-XXXX" type="password" data-testid="w9-ssn" /></div>
                <div><Label className="text-xs text-slate-500">Employer Identification Number</Label>
                  <Input value={form.ein} onChange={(e) => u("ein", e.target.value)} className="mt-1" placeholder="XX-XXXXXXX" data-testid="w9-ein" /></div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="flex gap-3 mt-6 p-5 bg-slate-50 rounded-xl border border-slate-200">
          <Button variant="outline" onClick={handleDownload} className="flex-1" data-testid="w9-download-btn">
            <Download className="w-4 h-4 mr-2" /> Download PDF
          </Button>
          <Button onClick={handleSave} disabled={saving} className="flex-1 bg-teal hover:bg-teal-dark text-white" data-testid="w9-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save to My Documents
          </Button>
        </div>
      </div>
    </DealerLayout>
  );
}
